import queryString from 'query-string';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef } from 'react';
import { CurrentImageProps, ImageType } from '@fabric-web/3d-viewer';
import * as globalState from '@/state';
import * as state from '@/components/AssetLabelling/state';
import { getCloudfrontUrl } from '@/utils/cloudfront';
import { GetStructureLocationsForAssetLabellingDataLoaderQuery } from '@/__generated__/graphql';
import { useCameraTargetDeeplink } from '@/hooks/deeplink/useCameraTargetDeeplink';
import { useCameraSettingsDeeplink } from '@/hooks/deeplink/useCameraSettingsDeeplink';
import { usePointCloudDeeplink } from '@/hooks/deeplink/usePointCloudDeeplink';
import { useVisibilityBoxDeeplink } from '@/hooks/deeplink/useVisibilityBoxDeeplink';
import { useViewpointsDeeplink } from '@/hooks/deeplink/useViewpointsDeeplink';
import { useZoneDeeplink } from '@/hooks/deeplink/useZoneDeeplink';
import { useLabelClassesDeeplink } from '@/hooks/deeplink/useLabelClassesDeeplink';
import { useCuboidClassesDeeplink } from '@/hooks/deeplink/useCuboidClassesDeeplink';
import { useCurrentCuboidDeeplink } from './deeplink/useCurrentCuboidDeeplink';

type AssetLabellingLocation =
  GetStructureLocationsForAssetLabellingDataLoaderQuery['allLocations'][number];

const mapLocationToCurrentImage = (
  location: AssetLabellingLocation | undefined,
  lookAt?: { x: number; y: number; z: number },
  fov?: number
): CurrentImageProps | undefined => {
  if (!location?.pose) return undefined;
  const { id, name, resourcePath, pose, imageSensor } = location;
  const baseImage: CurrentImageProps = {
    id,
    name,
    url: resourcePath ? getCloudfrontUrl(resourcePath) : '',
    position: [pose.x, pose.y, pose.z],
    rotation: [pose.roll, pose.pitch, pose.yaw],
    imageType: ImageType.Spherical,
    ...(lookAt ? { lookAt } : {}),
    ...(fov ? { fov } : {}),
  };
  if (imageSensor) {
    return {
      ...baseImage,
      imageType: ImageType.Rectilinear,
      width: imageSensor.width,
      height: imageSensor.height,
      focalLength: imageSensor.focalLength,
      cx: imageSensor.cx,
      cy: imageSensor.cy,
    };
  }
  return {
    ...baseImage,
    imageType: ImageType.Spherical,
    imageYawOffset: pose.yawOffset,
  };
};

export const useAssetLabellingDeeplink = () => {
  const [statesLoaded, setStatesLoaded] = useAtom(globalState.statesLoaded);
  const locations = useAtomValue(state.locations);
  const zones = useAtomValue(globalState.zones);
  const cuboids = useAtomValue(state.cuboids);

  const setNextSelectedSpherical = useSetAtom(globalState.viewerCurrentSpherical);

  const shouldUpdateUrl = statesLoaded;
  const isComplete = useRef(false);

  const onSphericalSelected = useCallback(
    (image: CurrentImageProps) => {
      setNextSelectedSpherical(image);
    },
    [setNextSelectedSpherical]
  );

  const { applyFromParsed: applyCameraTarget } = useCameraTargetDeeplink({
    shouldUpdateUrl,
    cameraTargetAtom: globalState.cameraTarget,
    selectedSphericalAtom: globalState.viewerNextCurrentSpherical,
    locations,
    mapLocationToCurrentImage,
    onSphericalSelected,
  });

  const { applyFromParsed: applyCameraSettings } = useCameraSettingsDeeplink({
    shouldUpdateUrl,
    cameraTypeAtom: globalState.cameraType,
    cameraDirectionAtom: globalState.cameraDirection,
  });

  const { applyFromParsed: applyPointCloud } = usePointCloudDeeplink({
    shouldUpdateUrl,
    pointBudgetAtom: globalState.pointBudget,
    enableAdaptivePointBudgetAtom: globalState.enableAdaptivePointBudget,
    edlStrengthAtom: globalState.edlStrength,
    rgbBlendLevelAtom: globalState.rgbBlendLevel,
  });

  const { applyFromParsed: applyVisibilityBox } = useVisibilityBoxDeeplink({
    shouldUpdateUrl,
    currentVisibilityBoxAtom: globalState.currentVisibilityBox,
  });

  const { applyFromParsed: applyViewpoints } = useViewpointsDeeplink({
    shouldUpdateUrl,
    showViewpointsAtom: state.showViewpoints,
  });

  const { applyFromParsed: applyZone } = useZoneDeeplink({
    shouldUpdateUrl,
    selectedZoneAtom: globalState.selectedZone,
    showZoneBoundariesAtom: state.showZoneBoundaries,
    showNeighbouringZonesAtom: state.showNeighbouringZones,
    zones,
  });

  const { applyFromParsed: applyLabelClasses } = useLabelClassesDeeplink({
    shouldUpdateUrl,
    labelClassByIdAtom: state.labelClassById as any,
  });

  const { applyFromParsed: applyCuboidClasses } = useCuboidClassesDeeplink({
    shouldUpdateUrl,
    cuboidClassByIdAtom: state.cuboidClassById as any,
  });

  const { applyFromParsed: applyCurrentCuboid } = useCurrentCuboidDeeplink({
    shouldUpdateUrl,
    currentCuboidAtom: state.currentCuboid,
    cuboids,
    isCuboidVisibilityBoxEnabledAtom: globalState.isCuboidVisibilityBoxEnabled,
  });

  useEffect(() => {
    if (isComplete.current || statesLoaded) return;
    if (!locations || !zones) return;

    const parsed = queryString.parse(window.location.search, { arrayFormat: 'comma' });

    applyCameraTarget(parsed);
    applyCameraSettings(parsed);
    applyPointCloud(parsed);
    applyVisibilityBox(parsed);
    applyViewpoints(parsed);
    applyZone(parsed);
    applyLabelClasses(parsed);
    applyCuboidClasses(parsed);
    applyCurrentCuboid(parsed);

    isComplete.current = true;
    setStatesLoaded(true);
  }, [
    statesLoaded,
    locations,
    zones,
    setStatesLoaded,
    applyCameraTarget,
    applyCameraSettings,
    applyPointCloud,
    applyVisibilityBox,
    applyViewpoints,
    applyZone,
    applyLabelClasses,
    applyCuboidClasses,
    applyCurrentCuboid,
  ]);
};
